import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { PATH_EMAIL_RECIPIENTS } from '../../routes/paths';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCustomSelectStyles } from '../../common/styles-select';
import useSettings from '../../hooks/useSettings';
import apiClient from '../../config/apiClient';

interface EmailRecipient {
  id: number;
  email: string;
  tenantId: string;
  tenant: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Tenant {
  id: string;
  name: string;
}

const EmailRecipientTable: React.FC = () => {
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>([]);
  const [filteredEmailRecipients, setFilteredEmailRecipients] = useState<EmailRecipient[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [emailRecipientToDelete, setEmailRecipientToDelete] = useState<number | null>(null);
  const { themeMode } = useSettings();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await apiClient.get('/tenants');
        setTenants(response.data);
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    };

    fetchTenants();
  }, []);

  useEffect(() => {
    const fetchEmailRecipients = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get('/email/recipients/');
        setEmailRecipients(response.data);
        setFilteredEmailRecipients(response.data);
      } catch (error) {
        setError('Error fetching email recipients');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailRecipients();
  }, []);

  const handleTenantChange = (option: any) => {
    const tenantId = tenants.find(tnt => tnt.id.toString() === option.value.toString())?.id
    const filtered = emailRecipients.filter((recipient) => recipient.tenantId === tenantId);
    setFilteredEmailRecipients(filtered);
  };

  const handleDelete = async () => {
    if (emailRecipientToDelete === null) return;

    try {
      await apiClient.delete(`/email/recipients/${emailRecipientToDelete}`);
      setEmailRecipients((prev) => prev.filter((recipient) => recipient.id !== emailRecipientToDelete));
      setFilteredEmailRecipients((prev) => prev.filter((recipient) => recipient.id !== emailRecipientToDelete));
      setOpen(false);
      toast.success('Email recipient deleted successfully!', {
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error deleting email recipient:', error);
    }
  };

  const handleOpenDeleteModal = (id: number) => {
    setEmailRecipientToDelete(id);
    setOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setOpen(false);
    setEmailRecipientToDelete(null);
  };

  const handleAddEmailRecipient = () => {
    navigate(PATH_EMAIL_RECIPIENTS.general.create);
  };

  const combinedSelectStyles = getCustomSelectStyles(themeMode);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', mt: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Email Recipients</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddEmailRecipient}
        >
          Add Email Recipient
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 3 }}>
        <label htmlFor="tenant-select">Filter by Tenant:</label>
        <Select
          onChange={(option: any) => handleTenantChange(option)}
          options={tenantOptions}
          styles={combinedSelectStyles}
        />
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Tenant ID</TableCell>
            <TableCell>Tenant Name</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Updated At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEmailRecipients.map((recipient) => (
            <TableRow key={recipient.id}>
              <TableCell>{recipient.id}</TableCell>
              <TableCell>{recipient.email}</TableCell>
              <TableCell>{recipient.tenantId}</TableCell>
              <TableCell>{recipient.tenant?.name}</TableCell>
              <TableCell>{new Date(recipient.createdAt).toLocaleDateString()}</TableCell>
              <TableCell>{new Date(recipient.updatedAt).toLocaleDateString()}</TableCell>
              <TableCell align="right">
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDeleteModal(recipient.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={open}
        onClose={handleCloseDeleteModal}
      >
        <DialogTitle>Delete Email Recipient</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this email recipient? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailRecipientTable;
